import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { toHexBytes } from '../../../lib/securityAccess/hexUtils';
import { computeTrace } from '../../../lib/securityAccess/algorithms';

// Renders the seed -> key derivation as a vertical, byte-by-byte hex trace, e.g.:
//   SEED        12 34 56 78
//        v XOR  A5 A5 A5 A5
//               B7 91 F3 DD
//        v +    12 34 56 78
//   KEY         C9 C6 4A 55
// Re-renders live from `seed`/`algorithmId`/`param`/`algoOpts` — it never holds its own
// state.
const Row = ({ label, bytes, muted }) => (
  <div className="flex items-baseline gap-3 font-mono text-xs md:text-sm">
    <span className={`w-14 shrink-0 uppercase tracking-wider ${muted ? 'text-muted-foreground' : 'font-bold'}`}>{label}</span>
    <span className={`tracking-widest ${muted ? 'text-muted-foreground' : ''}`}>{bytes.join(' ')}</span>
  </div>
);

const OpRow = ({ opLabel, operandBytes }) => (
  <div className="flex items-baseline gap-3 font-mono text-xs md:text-sm text-autoxium-blue">
    <span className="w-14 shrink-0 text-right">&darr; {opLabel}</span>
    {operandBytes && <span className="tracking-widest">{operandBytes.join(' ')}</span>}
  </div>
);

const AlgorithmVisualizer = ({ seed, algorithmId, param, algoOpts }) => {
  if (seed === null) {
    return (
      <Card className="technical-card" data-testid="sa-visualizer">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-base">Algorithm Visualizer</CardTitle>
          <CardDescription>Request a seed to see the step-by-step key derivation.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (seed === 0) {
    return (
      <Card className="technical-card" data-testid="sa-visualizer">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-base">Algorithm Visualizer</CardTitle>
          <CardDescription>Seed 00 00 00 00 — the ECU reports it is already unlocked, so no key is computed.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  let trace = null;
  let error = null;
  try {
    trace = computeTrace(algorithmId, seed, param, algoOpts);
  } catch (err) {
    error = err.message;
  }

  return (
    <Card className="technical-card" data-testid="sa-visualizer">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-base">Algorithm Visualizer</CardTitle>
        <CardDescription>Byte-by-byte trace of how this seed becomes the expected key.</CardDescription>
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-sm text-destructive" data-testid="sa-visualizer-error">Formula error: {error}</p>
        ) : (
          <div className="space-y-1.5 bg-muted/30 rounded-md p-4">
            <Row label="SEED" bytes={toHexBytes(seed)} muted />
            {trace.rows.map((row, i) => (
              <React.Fragment key={i}>
                <OpRow opLabel={row.opLabel} operandBytes={row.operandBytes} />
                {i < trace.rows.length - 1 && <Row label="" bytes={row.resultBytes} muted />}
              </React.Fragment>
            ))}
            <Row label="KEY" bytes={toHexBytes(trace.keyNum)} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AlgorithmVisualizer;
